import s from "./primitives.module.css";

/**
 * A NUMBER THE SERVER MAY NOT HAVE, rendered so that "we do not know" can never be read as "zero".
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A PRIMITIVE AND NOT A CALL TO `format.ts` AT EACH SITE
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * `null` is the DEFAULT CASE on this surface, not an edge. Measured against the live board on 2026-08-12:
 * `priceUsd` was null on 247 of 250 rows, `marketCapUsd` and `fdvUsd` on 250 of 250, and `priceChangePct` on
 * every row of every window. A screen that renders those as `0` states a price of zero for almost every token
 * it lists — a number a user can act on, that nobody computed.
 *
 * So every nullable number on screen goes through here, and this component guarantees three things a call
 * site cannot forget:
 *
 *   1. `null`/`undefined`/non-finite renders the UNKNOWN MARKER, never `0`, never an empty cell (an empty cell
 *      reads as a rendering bug, and a bug is easier to dismiss than an absence).
 *   2. IT CARRIES THE REASON. `title` says WHY the number is missing, in words — "no trusted price yet" is a
 *      coverage statement; "nothing traded in this window" is a different one; a user who cannot tell them
 *      apart concludes the terminal is broken.
 *   3. Tabular figures, so a column of numbers re-rendering at the tip does not jitter. A jittering number
 *      reads as a broken number.
 *   4. AN EXACT ZERO IS A THIRD STATE, marked `data-zero` and carrying its own words. `0` is a claim on a
 *      trading terminal — this moved nothing, this executed at nothing — and it must be sayable whether that
 *      claim was measured or merely defaulted into existence. See `NumProps.zero`.
 *
 * IT NEVER FORMATS. The formatter is passed in (`usd`, `price`, `num`, `pct`, `chgPct` from `lib/format.ts`)
 * so there is still exactly one implementation of every number's spelling.
 */
export interface NumProps {
  value: number | null | undefined;
  /** From `lib/format.ts`. Its own null handling is never reached — this component branches first — but the
   *  signature is the same so the two can never disagree about a formatted value. */
  format: (n: number | null | undefined) => string;
  /** WHY it is missing, in words. Shown on hover, and it is required: an unlabelled "—" is the same silence
   *  as a zero, one step quieter. */
  unknown: string;
  /**
   * WHAT AN EXACT ZERO MEANS HERE, in words — the counterpart of `unknown`, and it exists because ZERO IS
   * THE OTHER SILENT STATE.
   *
   * `—` and `0` are already different glyphs, so the two are not confusable by eye; what is confusable is a
   * zero that MEANS something (this leg moved no tokens — a real, measured quantity) and a zero that arrived
   * because something upstream had nothing to say and a `?? 0` filled it in. The reader cannot tell those
   * apart, and a terminal that says "0" without saying which is stating a measurement it may not have.
   *
   * So a call site that can legitimately show a zero has to be able to SAY SO, and one that shows a zero it
   * cannot explain gets the honest default below. Optional rather than required because most numbers on this
   * surface — a price, a market cap, a percentage — have no meaningful zero at all, and requiring words for
   * a case that cannot arise is how required props become copy-pasted noise.
   */
  zero?: string;
  /** The exact value on hover, when the visible one is compacted ("1.2M"). */
  title?: string;
  className?: string;
}

/** The one spelling of "we do not have this". One character, so a dense table stays readable, and it is a
 *  non-breaking em dash so it can never wrap alone onto its own line. */
export const UNKNOWN = "—";

/** What an unexplained zero says on hover. Deliberately not silent: see `NumProps.zero`. */
export const ZERO_UNEXPLAINED = "exactly zero, as measured — not a missing value";

export function Num({ value, format, unknown, zero, title, className }: NumProps) {
  const known = value != null && Number.isFinite(value);
  const classes = [s.amount, className].filter(Boolean).join(" ");
  if (!known) {
    return (
      <span className={classes} title={unknown} data-unknown="true">
        {UNKNOWN}
      </span>
    );
  }
  // AN EXACT ZERO IS MARKED IN THE MARKUP, not merely formatted. `data-zero` is what lets a test ask "is
  // this cell a measured zero or an absence?" — the question the previous audit of this surface could not
  // ask, so it counted em dashes and concluded the tape was clean while it was rendering zeros.
  if (value === 0) {
    return (
      <span className={classes} title={zero ?? ZERO_UNEXPLAINED} data-zero="true">
        {format(0)}
      </span>
    );
  }
  return (
    <span className={classes} title={title ?? String(value)}>
      {format(value)}
    </span>
  );
}
